<template>
  <div>
    <div class="object-titlebox">
      <div class="object-titlebox-left" style="padding-left: 5px">
        <!-- 戻る -->
        <button class="back-button" type="button" @click="$emit('close')">
          <img src="../../assets/icon_arrow_blue.svg" />
          <span class="normal-command-text" v-t="'message.button_back'"></span>
        </button>
      </div>
    </div>

    <main class="chat-main" ref="scrollArea">
      <!-- 先頭に1つだけ日付チップ（sticky） -->
      <div v-if="topDateLabel" class="sticky-date">
        {{ topDateLabel }}
      </div>

      <!-- メッセージをそのまま時系列で表示（グルーピングなし） -->
      <div
        v-for="m in localMessages"
        :key="m.id"
        class="message-row"
        :class="m.from === 'me' ? 'me' : 'other'"
      >
        <div class="msg" :class="m.from === 'me' ? 'me' : 'other'">
          <div class="meta-top">
            <span class="sender">{{ senderOf(m) }}</span>
            <span class="time">{{ formatTime(m.at) }}</span>
          </div>
          <!-- 色を切り替えたいバブル -->
          <div class="bubble" :class="m.from === 'me' ? 'bubble-me' : 'bubble-other'">
            <div class="bubble-text" v-html="nl2br(escapeHtml(m.text))"></div>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import { FEATURE_BD_2191_FIX_CALL_RECORD_DISPLAY } from "@/components/feature_toggles/feature_toggle";

export default {
  name: "LineLikeChat",
  props: {
    partner: { type: Object, default: () => ({ name: "相手", avatar: "" }) },
    // [{ id, from:'me'|'other', text, at: Date, senderName? }]
    messages: { type: Array, default: () => [] },
    // 右側（自分）表示名
    selfLabel: { type: Object, default: () => ({ name: "あなた", avatar: "" }) },
  },
  data() {
    return { localMessages: [] };
  },
  computed: {
    // 先頭メッセージの日付を 1 つだけ表示
    topDateLabel() {
      if (!this.localMessages.length) return "";
      let recordDate;
      if (FEATURE_BD_2191_FIX_CALL_RECORD_DISPLAY) {
        const firstMessageWithAt = this.localMessages.find((message) => message.at);
        if (!firstMessageWithAt) return "";
        const at = firstMessageWithAt.at;
        recordDate = at instanceof Date ? at : new Date(at);
        if (Number.isNaN(recordDate.getTime())) return "";
      } else {
        recordDate =
          this.localMessages[0].at instanceof Date
            ? this.localMessages[0].at
            : new Date(this.localMessages[0].at);
      }
      const y = recordDate.getFullYear();
      const m = ("0" + (recordDate.getMonth() + 1)).slice(-2);
      const day = ("0" + recordDate.getDate()).slice(-2);
      return `${y}/${m}/${day}`;
    },
  },
  watch: {
    messages: {
      immediate: true,
      deep: true,
      handler(v) {
        this.localMessages = (v || []).map((m) => ({ ...m }));
        this.$nextTick(this.scrollToBottom);
      },
    },
  },
  methods: {
    senderOf(m) {
      if (m.from === "me") return this.selfLabel || "自分";
      return m.senderName || this.partner?.name || "相手";
    },
    scrollToBottom() {
      const el = this.$refs.scrollArea;
      if (el) el.scrollTop = el.scrollHeight;
    },
    formatTime(d) {
      if (FEATURE_BD_2191_FIX_CALL_RECORD_DISPLAY && !d) return "";
      const dt = d instanceof Date ? d : new Date(d);
      if (FEATURE_BD_2191_FIX_CALL_RECORD_DISPLAY && Number.isNaN(dt.getTime())) return "";
      const hh = ("0" + dt.getHours()).slice(-2);
      const mm = ("0" + dt.getMinutes()).slice(-2);
      return `${hh}:${mm}`;
    },
    escapeHtml(s) {
      return String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    },
    nl2br(s) {
      return String(s).replace(/\n/g, "<br/>");
    },
  },
  mounted() {
    this.scrollToBottom();
  },
};
</script>

<style scoped>
/* 本文 */
.chat-main {
  height: calc(100vh - 240px);
  overflow-y: auto;
  padding: 8px 12px 20px;
  position: relative;
}

/* 先頭だけ見せる日付チップ */
.sticky-date {
  position: sticky;
  top: 8px; /* ヘッダーの下にピタッ */
  z-index: 2;
  display: inline-block;
  margin: 6px auto 12px;
  left: 50%;
  transform: translateX(-50%);
  padding: 4px 10px;
  font-size: 12px;
  color: var(--muted);
  background: var(--chip-bg);
  border: 1px solid var(--chip-border);
  border-radius: 12px;
  backdrop-filter: blur(2px);
}

/* 行・位置揃え */
.message-row {
  margin: 6px 6px 16px;
  display: flex;
}
.message-row.other {
  justify-content: flex-start;
}
.message-row.me {
  justify-content: flex-end;
}

/* メッセージ枠（メタ＆バブルの親） */
.msg {
  max-width: min(82vw, 560px);
  display: flex;
  flex-direction: column;
}
.msg.other {
  align-items: flex-start;
}
.msg.me {
  align-items: flex-end;
}

/* 名前と時刻（枠内で左右に配置） */
.meta-top {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}
.sender,
.time {
  font-size: 12px;
  color: var(--muted);
}

/* バブル */
.bubble {
  width: auto;
  min-width: 150px; /* 好きな最小幅に調整 */
  max-width: 100%; /* はみ出し防止用 */

  border-radius: 14px;
  padding: 10px 12px;
  line-height: 1.6;
  word-break: break-word;
  white-space: pre-wrap;
  box-shadow: 0 1px 0 rgba(0, 0, 0, 0.2);
}
.bubble-me {
  color: #5171cb;
  background-color: #ecf1fa;
}
.bubble-other {
  background: #f6f6f6;
}

/* 本文 */
.bubble-text {
  font-size: 14px;
}
</style>
